import React from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { FileText, Search, Clock, CheckCircle, ShieldCheck, AlertCircle } from 'lucide-react';

const milestones = [
  { icon: FileText, title: 'Brand Name Application Filed', time: 'Day 1', description: 'Your brand name application hits the USPTO after we complete a thorough search and final review.' },
  { icon: Search, title: 'USPTO Examination', time: 'Month 3-4', description: 'An examining attorney evaluates your brand name filing for compliance with federal trademark law.' },
  { icon: AlertCircle, title: 'Office Action (if any)', time: 'Month 4-6', description: 'If objections surface about your brand name, we respond with targeted legal arguments to keep your case alive.', accent: true },
  { icon: Clock, title: 'Publication Period', time: 'Month 6-8', description: 'Your brand name is published for a 30-day window where third parties can oppose the registration.' },
  { icon: CheckCircle, title: 'Notice of Allowance', time: 'Month 8-10', description: 'No opposition? You receive formal notice that your brand name registration is proceeding.' },
  { icon: ShieldCheck, title: 'Brand Name Certificate Issued', time: 'Month 10-12', description: 'Your federal brand name trademark certificate arrives. Nationwide protection is now yours.' },
];

const RegistrationTimeline = () => {
  const header = useScrollAnimation({ threshold: 0.15 });
  return (
    <section data-testid="timeline-section" className="py-24 md:py-32 bg-[#041B1C] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div ref={header.ref} className={`lg:sticky lg:top-32 scroll-fade-up ${header.isVisible ? 'scroll-visible' : ''}`}>
            <span className="inline-block bg-[#A5BF23]/10 text-[#A5BF23] px-4 py-1.5 rounded-full text-xs font-bold tracking-[0.2em] mb-4">TIMELINE</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F0F4F4] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>From Brand Name Filing to Federal Certificate</h2>
            <p className="text-base text-[#A0B2B2] leading-relaxed mb-6">Here is what happens at each stage after we submit your brand name trademark application to the USPTO.</p>
            <div className="bg-[#093336]/60 backdrop-blur-sm border border-white/5 rounded-2xl p-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-[#A5BF23]/5 rounded-xl">
                  <div className="text-3xl font-bold text-[#A5BF23]" style={{ fontFamily: "'Outfit', sans-serif" }}>8-12</div>
                  <div className="text-xs text-[#A0B2B2] mt-1">Months Total</div>
                </div>
                <div className="text-center p-4 bg-[#A5BF23]/5 rounded-xl">
                  <div className="text-3xl font-bold text-[#A5BF23]" style={{ fontFamily: "'Outfit', sans-serif" }}>6</div>
                  <div className="text-xs text-[#A0B2B2] mt-1">Key Stages</div>
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-0">
            {milestones.map((m, i) => <TimelineStep key={i} milestone={m} index={i} isLast={i === milestones.length - 1} />)}
          </div>
        </div>
      </div>
    </section>
  );
};

const TimelineStep = ({ milestone, index, isLast }) => {
  const step = useScrollAnimation({ threshold: 0.15 });
  const Icon = milestone.icon;
  return (
    <div ref={step.ref} className={`relative scroll-fade-up scroll-delay-${Math.min(index + 1, 6)} ${step.isVisible ? 'scroll-visible' : ''}`}>
      <div className="flex gap-5">
        <div className="flex flex-col items-center">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 z-10 ${milestone.accent ? 'bg-amber-500/20 border border-amber-500/30' : 'bg-[#A5BF23]/10 border border-[#A5BF23]/20'}`}>
            <Icon className={`w-5 h-5 ${milestone.accent ? 'text-amber-400' : 'text-[#A5BF23]'}`} strokeWidth={1.5} />
          </div>
          {!isLast && <div className="w-[1px] flex-1 bg-gradient-to-b from-[#A5BF23]/20 to-transparent mt-2" />}
        </div>
        <div className="pb-10">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#A0B2B2]/60 block mb-1">{milestone.time}</span>
          <h3 className="text-lg font-bold text-[#F0F4F4] mb-1.5" style={{ fontFamily: "'Outfit', sans-serif" }}>{milestone.title}</h3>
          <p className="text-sm text-[#A0B2B2] leading-relaxed max-w-md">{milestone.description}</p>
        </div>
      </div>
    </div>
  );
};

export default RegistrationTimeline;
